/* eslint-disable max-len */
import { DispensationRow } from 'components/dispenser/dispensation/dispensation_types';
import { v4 as uuid } from 'uuid';
import { SUSPENDED_CARDSTATUS_TEXT } from 'utility/constants';
import moment from 'moment';
import {
  OrderType, OrderRoutesArray, OrderCancelState, ContentUserTypes, patientApplicationStatus
} from 'utility/enumerations';

export const isEmpty = (obj) => {
  if (obj === false) return false;

  if (!obj && obj !== 0) return true;

  if (Array.isArray(obj) && obj.length === 0) return true;

  if (Object.keys(obj).length === 0 && obj.constructor === Object) return true;

  return false;
};

export const yesNo = (bool) => (bool === undefined ? '' : bool ? 'Yes' : 'No');
export const isBoolean = (input) => typeof input === 'boolean';

const trimObject = (obj) => {
  if (obj && obj.constructor === Object) {
    return Object.keys(obj).reduce((result, key) => {
      const keyTrimmed = key.trim();
      if (isBoolean(obj[key])) {
        result[keyTrimmed] = obj[key];
      }
      if (obj[key] && obj[key].trim) {
        const valTrimmed = obj[key].trim();
        result[keyTrimmed] = valTrimmed;
      }
      return result;
    }, {});
  }

  return obj;
};

export const trimAll = (obj) => {
  if (obj) {
    if (typeof obj === 'string') {
      return obj.trim();
    } if (Array.isArray(obj)) {
      const trimmedArray = obj;
      trimmedArray.forEach(function (part, index) {
        this[index] = trimAll(part);
      }, trimmedArray);
      return trimmedArray;
    } if (obj.constructor === Object) {
      return trimObject(obj);
    }
    return obj;
  } if (obj === undefined) {
    return null;
  }
  return obj;
};

export const flatPermissionsArray = (permissions) => {
  const flatArray = [];
  permissions.forEach((permission) => {
    if (permission.children.length >= 1) {
      flatArray.push(
        { ...permission, children: [] },
        ...flatPermissionsArray(permission.children)
      );
    } else {
      flatArray.push({ ...permission, children: [] });
    }
  });
  // Removes the children property from the permission Object
  return flatArray.map((permission) => {
    delete permission.children;
    return permission;
  });
};

export const hasAccessToThisRoute = (permissions, id) => {
  const flatPermissions = flatPermissionsArray(permissions);
  if (Array.isArray(id)) {
    return flatPermissions.some((permission) => id.includes(permission.permissionId));
  }
  return flatPermissions.some((permission) => permission.permissionId === id);
};

export const getNumericAmountRemaining = (routes) => {
  const newRoutes = routes.map((route) => {
    const [number, unit] = route.amountRemaining.split(' ');
    return {
      routeId: route.routeId,
      amount: {
        numeric: Number(number.replaceAll(',', '')),
        unit
      }
    };
  });
  return newRoutes;
};

export const getNumericAmountNotToExceed = (routes) => {
  const newRoutes = routes.map((route) => {
    const [number, unit] = route.amountNotToExceed.split(' ');
    return {
      routeId: route.routeId,
      amount: {
        numeric: Number(number.replaceAll(',', '')),
        unit
      }
    };
  });
  return newRoutes;
};

export const sumAllAmounts = (rows, routeId) => {
  const result = rows.reduce((sum, row) => (row.route === routeId ? sum + row.amount : sum + 0), 0);
  return result;
};

export const getGeneralAmountRemaining = (routes, routeId) => {
  const numericAmount = getNumericAmountRemaining(routes);
  return numericAmount.filter((route) => route.routeId === routeId)[0].amount.numeric;
};

export const getGeneralAmountNotToExceed = (routes, routeId) => {
  const numericAmount = getNumericAmountNotToExceed(routes);
  return numericAmount.filter((route) => route.routeId === routeId)[0].amount.numeric;
};

export const isValidDispensation = (rows: DispensationRow[], amountRemaining, routes, nonSmokingAggregateAmountRemaining, isMedicalMarijuanaOrder) => {
  const remainingAmountNotExceeded = amountRemaining.reduce((notExceedsLimits, route) => {
    const difference = getGeneralAmountRemaining(routes, route.routeId) - sumAllAmounts(rows, route.routeId);
    if (difference < 0) {
      return notExceedsLimits && false;
    }
    return notExceedsLimits && true;
  }, true);

  const amountNotToExceedNotExceeded = amountRemaining.reduce((notExceedsLimit, route) => {
    const difference = getGeneralAmountNotToExceed(routes, route.routeId) - sumAllAmounts(rows, route.routeId);
    if (difference < 0) {
      return notExceedsLimit && false;
    }
    return notExceedsLimit && true;
  }, true);

  const totalAmount = rows.reduce((sum, route) => (route.route !== '' ? route.amount + sum : sum), 0);
  const totalDevices = rows.reduce((sum, route) => (route.route !== '' ? route.numberOfDeliveryDevices + sum : sum), 0);
  const allRowsWithNumDevicesHaveDevice = rows.reduce((result, route) => {
    if (route.numberOfDeliveryDevices !== 0 && !route.deliveryDevice) {
      return false;
    }
    return result;
  }, true);

  const allRowsHaveRoute = rows.reduce((result, route) => {
    if (route.route === '') {
      return false;
    }
    return result;
  }, true);

  const isTotalAmountGreaterThanZero = allRowsHaveRoute && (totalAmount > 0 || (totalDevices > 0 && allRowsWithNumDevicesHaveDevice));

  const nonSmokingAggregateNotExceeded = isMedicalMarijuanaOrder ? totalAmount <= nonSmokingAggregateAmountRemaining : true;

  const isValid = {
    remainingAmountNotExceeded: remainingAmountNotExceeded && amountNotToExceedNotExceeded && nonSmokingAggregateNotExceeded,
    isTotalAmountGreaterThanZero
  };
  return isValid;
};

export const isLegalAge = (dob) => {
  return Math.floor((new Date().getTime() - new Date(dob).getTime()) / 3.15576e+10) >= 18;
};

// Sorts alphabetically an array of objects based on one of the keys
export const sortAlphabetically = (array, key) => {
  const newArray = array.sort((a, b) => {
    const textA = a[key]?.toUpperCase();
    const textB = b[key]?.toUpperCase();
    return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
  });
  return newArray;
};

export const isMinorWithNoActiveCaregivers = (dob, caregivers) => {
  return !isLegalAge(dob)
    && caregivers?.filter((caregiver) => caregiver.hasActiveCard).length === 0;
};

export const isMinorWithNoCaregivers = (dob, caregivers) => {
  return !isLegalAge(dob)
    && caregivers?.length < 1;
};

export const getCountyNameAndId = (name, counties) => {
  return counties?.find((county) => county.name === name);
};

// 4: Processing, 2: Processed
export const isPaymentStatusProcessedOrProcessing = (status) => {
  return status === 2 || status === 4;
};

export const mapCertStatusIconContent = (status) => {
  if (status === 'Active') {
    return {
      toolTipText: 'All of your patients who have Active or Scheduled Certifications.',
      toolTipIcon: 'id-card'
    };
  }
  if (status === 'Uncertified') {
    return {
      toolTipText: 'Patients who have no Certifications or only Closed or Cancelled Certifications.',
      toolTipIcon: 'calendar-xmark'
    };
  }
  return {
    toolTipText: 'Patients who have an active Certification that will expire in the next 35 days.',
    toolTipIcon: 'clock-four'
  };
};

export const getQueryString = (options) => {
  return options
    ? Object.keys(options)
      .map((eachOption) => `${eachOption}=${options[eachOption]}`)
      .join('&')
    : '';
};

// returns the number of days between two dates
export const getDurationDays = (startDate, endDate) => {
  const _MS_PER_DAY = 1000 * 60 * 60 * 24;
  const start = new Date(startDate);
  const end = new Date(endDate);
  // Discard the time and time-zone information.
  const utcStart = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const utcEnd = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());

  return Math.floor((utcEnd - utcStart) / _MS_PER_DAY);
};

// Includes today's date
export const isTodayOrFutureDate = (startDate) => {
  const today = moment().format('MM/DD/YYYY');
  return getDurationDays(today, startDate) >= 0;
};

export const getStartDate = (startDate) => {
  const today = moment().format('MM/DD/YYYY');
  return getDurationDays(today, startDate) >= 0 ? startDate : today;
};

export const splitNewOrders = (startDate, endDate, orderType, orderData) => {
  const totalDays = getDurationDays(startDate, moment(endDate).add(1, 'days').format('MM/DD/YYYY'));

  const durationIncrement = orderType === OrderType['Smoking Marijuana'] ? 35 : 70;
  const intFloor = Math.floor(totalDays / durationIncrement);
  // eslint-disable-next-line prefer-const
  let orders = [];
  let lastStartDay = startDate;
  let lastEndDay = intFloor === 0 ? endDate : moment(startDate).add((durationIncrement - 1), 'days').format('MM/DD/YYYY');

  for (let i = 1; i <= intFloor; i += 1) {
    orders.push({
      orderId: uuid(),
      status: 'Not Saved',
      startDate: lastStartDay,
      endDate: lastEndDay,
      orderDuration: durationIncrement,
      note: orderData.note,
      cancelledStatus: null,
      canReinstate: true,
      routes: orderData.routes.filter((route) => route.amount > 0).map((route) => ({
        ...route,
        dispensedAmount: 0,
        removeRoute: false
      }))
    });
    lastStartDay = moment(lastStartDay).add((durationIncrement), 'days').format('MM/DD/YYYY');
    lastEndDay = i === intFloor ? endDate : moment(lastEndDay).add((durationIncrement), 'days').format('MM/DD/YYYY');
  }

  if (totalDays % durationIncrement > 0) {
    orders.push({
      orderId: uuid(),
      endDate: lastEndDay,
      status: 'Not Saved',
      startDate: lastStartDay,
      orderDuration: totalDays % durationIncrement,
      note: orderData.note,
      cancelledStatus: null,
      canReinstate: true,
      routes: orderData.routes.filter((route) => route.amount > 0).map((route) => ({
        ...route,
        dispensedAmount: 0,
        removeRoute: false
      }))
    });
  }
  return orders;
};

export const mapOrderSummaryFromFlatSummary = (dailyDoseLimits, groupedOrderType) => {
  // eslint-disable-next-line prefer-const
  let newOrderType = [];
  // eslint-disable-next-line guard-for-in
  for (const orderSequence in groupedOrderType) {
    const order = {
      orderId: groupedOrderType[orderSequence][0].orderIdEncrypted,
      startDate: moment(groupedOrderType[orderSequence][0].startDate).format('MM/DD/YYYY'),
      endDate: moment(groupedOrderType[orderSequence][0].startDate).add((groupedOrderType[orderSequence][0].durationDays - 1), 'days').format('MM/DD/YYYY'),
      orderDuration: groupedOrderType[orderSequence][0].durationDays,
      status: groupedOrderType[orderSequence][0].orderStatus,
      canReinstate: groupedOrderType[orderSequence][0].canReinstate,
      cancelledStatus: groupedOrderType[orderSequence][0].orderStatus === 'Cancelled' ? OrderCancelState.Cancel : null,
      note: groupedOrderType[orderSequence][0].notes,
      routes: groupedOrderType[orderSequence].map((route) => ({
        typeId: route.route && dailyDoseLimits.find((staticRoute) => route.route === staticRoute.formName).typeId,
        formId: route.route && dailyDoseLimits.find((staticRoute) => route.route === staticRoute.formName).formId,
        formName: route.route,
        additionalText: route.route
        && dailyDoseLimits.find((staticRoute) => route.route === staticRoute.formName).additionalText,
        nonSmokingMax: route.route
        && dailyDoseLimits.find((staticRoute) => route.route === staticRoute.formName).nonSmokingMax,
        smokingMax: route.route
        && dailyDoseLimits.find((staticRoute) => route.route === staticRoute.formName).smokingMax,
        amount: route.route && route.pendingAmount ? route.pendingAmount : route.dosage,
        deliveryDevice: route.route && route.purchaseDeliveryDevice,
        dispensedAmount: route.route && route.dispensedAmount,
        removeRoute: route.route == null,
        rfeNeeded: route.route && (route.pendingAmount && route.pendingAmount > 0),
        canRemove: route.route && route.canRemove
      })),
    };
    newOrderType.push(order);
  }
  return newOrderType;
};

export const getAvailableStartDate = (orders, certStartDate, certEndDate) => {
  if (orders.length > 0) {
    return getDurationDays(orders[orders.length - 1].endDate, certEndDate) > 0
      ? moment(orders[orders.length - 1].endDate).add(1, 'days').format('MM/DD/YYYY')
      : null;
  }
  return certStartDate;
};

export const areDatesValid = (orders, certStartDate, certEndDate) => {
  const valid = orders.reduce((acc, order) => {
    return acc
      && getDurationDays(certStartDate, order.startDate) >= 0
      && getDurationDays(order.endDate, certEndDate) >= 0;
  }, true);

  const notOverlappedDays = orders.reduce((acc, currentOrder, index, allOrders) => {
    if (index !== 0) {
      return acc && getDurationDays(allOrders[index - 1].endDate, currentOrder.startDate) > 0;
    }
    return true;
  }, true);

  return valid && notOverlappedDays;
};

export const getFlatOrders = (orderTypePropertyName, orderSummary) => {
  // eslint-disable-next-line prefer-const
  let newFlatArray = [];
  orderSummary[orderTypePropertyName]
    .forEach((order, index) => {
      newFlatArray.push(...sortAlphabetically(order.routes.filter((route) => !route.removeRoute), 'formName').map((route) => ({
        dosage: orderTypePropertyName === 'smokingOrders' && route.amount && typeof route.amount !== 'string' ? route.amount.toFixed(3) : route.amount,
        totalDosage: orderTypePropertyName === 'smokingOrders' ? route.amount : route.amount * order.orderDuration,
        orderSequence: index + 1,
        route: route.formName,
        rfeNeeded: route.rfeNeeded,
        amountbelowDispensed: route.amountbelowDispensed,
        totalAggregateExceeded: order.totalAggregateExceeded,
        purchaseDeliveryDevice: route.deliveryDevice,
        startDate: order.startDate,
        durationDays: order.orderDuration,
        notes: order.note,
        orderStatus: order.status,
        dispensedAmount: route.dispensedAmount,
        cancelledState: order.cancelledStatus,
        nonSmokingMax: route.nonSmokingMax,
        smokingMax: route.smokingMax
      })));
    });
  return newFlatArray;
};

export const sortSummaryOrder = (orders) => {
  // eslint-disable-next-line prefer-const
  let newSortedOrderType = [];
  let groupedOrdersBySecuence = [];
  let previousSecuence = 1;
  orders.forEach((order, index) => {
    if (order.orderSequence === previousSecuence) {
      groupedOrdersBySecuence.push(order);
    } else {
      newSortedOrderType.push(...sortAlphabetically(groupedOrdersBySecuence, 'route'));
      groupedOrdersBySecuence = [];
      previousSecuence = order.orderSequence;
      groupedOrdersBySecuence.push(order);
    }
    if (index === orders.length - 1) {
      newSortedOrderType.push(...sortAlphabetically(groupedOrdersBySecuence, 'route'));
    }
  });
  return newSortedOrderType;
};

export const toUSCurrency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

export const toFormattedUSCurrency = (num) => {
  return toUSCurrency.format(num)?.replace(/\s+/g, ' ');
};

export const getContentManagementUserTypeFromName = (userTypeName) => {
  if (userTypeName === 'Admin') {
    return ContentUserTypes.find((type) => type.name === 'Admins');
  }
  if (userTypeName === 'Card Holders') {
    return ContentUserTypes.find((type) => type.name === 'Card Holders');
  }
  if (userTypeName === 'Physician') {
    return ContentUserTypes.find((type) => type.name === 'Physicians');
  }
  return ContentUserTypes.find((type) => type.name === userTypeName);
};

export const getSelectedPermissionsId = (userTypeCategories) => {
  const selectedPermissions = userTypeCategories.reduce((accu, current) => {
    return [...accu, ...flatPermissionsArray(current.categoryPermissions).filter((permission) => permission.selected)];
  }, []);
  return selectedPermissions.map((permission) => permission.permissionId);
};

export const processPermissions = (permissions, selectedIds) => {
  const processedArray = permissions.map((permission) => ({
    ...permission,
    selected: selectedIds.some((id) => id === permission.permissionId),
    children: permission.children.length >= 1 ? [...processPermissions(permission.children, selectedIds)] : []
  }));
  return processedArray;
};

export const processUserTypeCategories = (userTypeCategories, selectedIds) => {
  return userTypeCategories.map((category) => ({
    ...category,
    categoryPermissions: processPermissions(category.categoryPermissions, selectedIds)
  }));
};

export const getProposedLimitsForRfe = (flatOrderSummary) => {
  const medMarijuanaRfeOrders = flatOrderSummary?.medicalMarijuanaOrders.filter((order) => order.orderStatus !== 'Expired');
  const smokingRfeOrders = flatOrderSummary?.smokingOrders.filter((order) => order.orderStatus !== 'Expired');

  const mappedMedMjOrders = medMarijuanaRfeOrders
    ? medMarijuanaRfeOrders?.filter((order) => order.cancelledState !== OrderCancelState.Cancel)?.map((order) => (
      {
        typeId: OrderType['Medical Marijuana'],
        formId: OrderRoutesArray.find((r) => r.name === order.route)?.id,
        nonSmokingMax: order.dosage,
        smokingMax: 0,
        orderDuration: order.durationDays
      }))
    : [];

  const mappedSmokingOrders = smokingRfeOrders
    ? smokingRfeOrders?.filter((order) => order.cancelledState !== OrderCancelState.Cancel)?.map((order) => (
      {
        typeId: OrderType['Smoking Marijuana'],
        formId: OrderRoutesArray.find((r) => r.name === order.route)?.id,
        nonSmokingMax: 0,
        smokingMax: order.dosage,
        orderDuration: order.durationDays
      }))
    : [];

  const returnArray = [
    ...mappedMedMjOrders,
    ...mappedSmokingOrders
  ];

  return returnArray;
};

export const getProposedMaxTotalForRfe = (proposedLimitsList: any[]) => {
  if (proposedLimitsList && proposedLimitsList.length > 0) {
    const formIds = proposedLimitsList.filter((rt) => rt && rt.formId && rt.formId > 0).map((rt) => rt.formId);
    let proposedTotal = 0;
    const countedFormIds = [];
    formIds.forEach((f) => {
      if (!countedFormIds.find((id) => id === f)) {
        const maxRouteAmountObject = proposedLimitsList
          .filter((route) => route.formId === f)
          .reduce((r, o) => { // find maximum amount of route form with formId
            const result = r.nonSmokingMax > o.nonSmokingMax
              ? r
              : o;
            r = result;
            return result;
          });
        // Sum
        proposedTotal += maxRouteAmountObject && maxRouteAmountObject.nonSmokingMax
          ? maxRouteAmountObject.nonSmokingMax
          : 0;
        countedFormIds.push(f);
      }
    });
    return Math.round(proposedTotal);
  }
  return 0;
};

export const determineCardholderCardStatus = (cardStatus, applicationStatus) => {
  return (applicationStatus.id === patientApplicationStatus.CardStatusInvalid
    || cardStatus === SUSPENDED_CARDSTATUS_TEXT)
    ? cardStatus
    : applicationStatus.name;
};

export const getApplicationSectionStatusText = (section, status) => {
  const sectionStatuses = {
    notSubmitted: 1,
    submited: 2,
    completed: 3,
    rejected: 4,
    Bypassed: 5
  };

  switch (section) {
    case 'demographics':
      return 'Completed';
    case 'photoStatus':
      return status === sectionStatuses.notSubmitted ? 'Not Submitted'
        : status === sectionStatuses.submited ? 'Submitted'
          : status === sectionStatuses.completed ? 'Approved' : 'Rejected';
    case 'proofStatus':
      return status === sectionStatuses.notSubmitted ? 'Not Submitted'
        : status === sectionStatuses.submited ? 'Submitted'
          : status === sectionStatuses.completed ? 'Approved' : 'Rejected';
    case 'signatureStatus':
      return status === sectionStatuses.notSubmitted ? 'Not Signed'
        : 'Signed';
    case 'paymentStatus':
      return status === sectionStatuses.notSubmitted ? 'Not Submitted'
        : status === sectionStatuses.submited ? 'Processing'
          : status === sectionStatuses.completed ? 'Processed' : 'Rejected';
    case 'printedStatus':
      return status === sectionStatuses.notSubmitted ? 'Not Printed'
        : status === sectionStatuses.completed ? 'Printed' : 'Deferred';
    default:
      return '';
  }
};

export const millisToMinutesAndSeconds = (millis) => {
  const minutes = Math.floor(millis / 60000);
  const seconds = ((millis % 60000) / 1000);

  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds.toFixed(0)}`;
};

export const getPostLoginRoute = (pathname) => {
  const location = pathname.replace('spa/', '');
  const isLocationLoginOrLogout = location === '/login' || location === '/login/sso' || location === '/logout';
  return isLocationLoginOrLogout ? '/' : location;
};

export const formatExportDate = (value: any): string => {
  if (!value) {
    return '';
  }

  const parsedDate = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getDate()).padStart(2, '0');
  const year = parsedDate.getFullYear();

  return `${month}/${day}/${year}`;
};
